import React from "react";
import { Link } from "react-router-dom";
import { Copy, Terminal, Shield, Zap, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import MarketingPageHero from "@/components/marketing/MarketingPageHero";
import { usePageMeta } from "@/lib/usePageMeta";

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "authentication", label: "Authentication" },
  { id: "endpoints", label: "Endpoints" },
  { id: "rate-limits", label: "Rate limits" },
  { id: "errors", label: "Error codes" },
  { id: "examples", label: "Examples" },
];

function CodeBlock({ code, lang = "bash", testId }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Copied");
    } catch { toast.error("Copy failed"); }
  };
  return (
    <div className="relative rounded-lg border border-[color:var(--lm-dark-border)] bg-[color:var(--lm-dark)] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[color:var(--lm-dark-border)] bg-white/[0.03]">
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/50">{lang}</span>
        <button
          onClick={copy}
          data-testid={testId}
          className="inline-flex items-center gap-1 text-[11px] font-mono text-white/60 hover:text-white transition-colors"
        >
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-xs md:text-sm text-white/90 font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function EndpointCard({ method, path, desc, testId }) {
  const cls = {
    GET: "bg-emerald-50 text-emerald-700 border-emerald-200",
    POST: "bg-blue-50 text-blue-700 border-blue-200",
    DELETE: "bg-red-50 text-red-700 border-red-200",
    PATCH: "bg-amber-50 text-amber-800 border-amber-200",
  }[method] || "bg-lm-tint text-lm-text2 border-lm-border";
  return (
    <div className="rounded-lg border border-lm-border bg-lm-surface p-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}>{method}</span>
        <code className="text-sm font-mono text-lm-text">{path}</code>
      </div>
      <p className="mt-2 text-sm text-lm-text2">{desc}</p>
    </div>
  );
}

// Flip to false to bring the full API documentation back online. While true,
// the page shows a "Coming soon" placeholder; all the real docs code below is
// kept intact (just not rendered).
const API_DOCS_COMING_SOON = true;

export default function ApiDocs() {
  usePageMeta(
    "API Docs — Leadmato",
    "The Leadmato public API — authenticate, run scans, fetch results and manage workspaces programmatically. REST + JSON, keys via the dashboard.",
  );

  if (API_DOCS_COMING_SOON) {
    return (
      <MarketingLayout>
        <MarketingPageHero
          eyebrow="Developer"
          title="API Docs — coming soon."
          subtitle="Programmatic access to Leadmato is on the way. Soon you'll be able to run scans and pull leads straight from your own code."
        >
          <div className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-lm-primary bg-lm-tint2 px-3 py-1.5 rounded-full">
            <Terminal className="w-4 h-4" /> Coming soon
          </div>
        </MarketingPageHero>
      </MarketingLayout>
    );
  }

  const curlExample = `curl -X POST "https://api.leadmato.com/v1/scans" \\
  -H "Authorization: Bearer lm_sk_live_xxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "query": "founders struggling with lead-gen in India",
    "platforms": ["reddit", "x", "linkedin"],
    "market_scope": "all_india"
  }'`;

  const pyExample = `import requests

r = requests.post(
    "https://api.leadmato.com/v1/scans",
    headers={"Authorization": "Bearer lm_sk_live_xxxxxxxxxxxx"},
    json={
        "query": "founders struggling with lead-gen in India",
        "platforms": ["reddit", "x", "linkedin"],
        "market_scope": "all_india",
    },
    timeout=60,
)
r.raise_for_status()
scan = r.json()
print(scan["scan_id"], len(scan["results"]))`;

  return (
    <MarketingLayout>
      <MarketingPageHero
        eyebrow="Developer"
        title="The Leadmato API."
        subtitle="REST endpoints for triggering scans, fetching results and managing your workspace from any language that speaks HTTP."
      >
        <Link
          to="/signup"
          data-testid="apidocs-cta-key"
          className="inline-flex items-center gap-2 bg-lm-brand-green text-[color:var(--lm-dark)] hover:bg-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
        >
          Get an API key <ArrowRight className="w-4 h-4" />
        </Link>
        <a
          href="#endpoints"
          className="text-sm font-medium text-white/85 hover:text-white"
        >
          Jump to endpoints →
        </a>
      </MarketingPageHero>

      <section className="py-12 md:py-16 bg-lm-bg">
        <div className="max-w-6xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3 mb-3">On this page</div>
            <nav className="flex lg:flex-col flex-wrap gap-1">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`apidocs-toc-${s.id}`}
                  className="text-sm text-lm-text2 hover:text-lm-primary hover:bg-lm-tint px-3 py-1.5 rounded-md transition-colors"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="space-y-14 min-w-0">
            {/* Overview */}
            <section id="overview" className="scroll-mt-24">
              <h2 className="font-display text-2xl font-semibold text-lm-text mb-3">Overview</h2>
              <p className="text-lm-text2 leading-relaxed">
                The Leadmato API is a JSON-over-HTTPS interface hosted at <code className="font-mono text-sm bg-lm-tint px-1.5 py-0.5 rounded">https://api.leadmato.com/v1</code>.
                Every request must be authenticated with a Bearer token. Every response is JSON. All timestamps are ISO-8601 in UTC.
              </p>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-lm-border bg-lm-surface p-4">
                  <Terminal className="w-4 h-4 text-lm-primary mb-2" />
                  <div className="text-sm font-semibold">REST + JSON</div>
                  <div className="text-xs text-lm-text3 mt-1">Standard verbs, standard status codes.</div>
                </div>
                <div className="rounded-lg border border-lm-border bg-lm-surface p-4">
                  <Shield className="w-4 h-4 text-lm-primary mb-2" />
                  <div className="text-sm font-semibold">Bearer auth</div>
                  <div className="text-xs text-lm-text3 mt-1">Workspace-scoped keys, rotatable from the dashboard.</div>
                </div>
                <div className="rounded-lg border border-lm-border bg-lm-surface p-4">
                  <Zap className="w-4 h-4 text-lm-primary mb-2" />
                  <div className="text-sm font-semibold">Same engine</div>
                  <div className="text-xs text-lm-text3 mt-1">Identical 5-lens pipeline as the web app.</div>
                </div>
              </div>
            </section>

            {/* Authentication */}
            <section id="authentication" className="scroll-mt-24">
              <h2 className="font-display text-2xl font-semibold text-lm-text mb-3">Authentication</h2>
              <p className="text-lm-text2 leading-relaxed">
                Create an API key from <span className="font-mono text-sm bg-lm-tint px-1.5 py-0.5 rounded">Settings → Developer</span> in the app.
                Keys look like <code className="font-mono text-sm bg-lm-tint px-1.5 py-0.5 rounded">lm_sk_live_&#8230;</code>. Send it in the <code className="font-mono text-sm bg-lm-tint px-1.5 py-0.5 rounded">Authorization</code> header on every request.
              </p>
              <div className="mt-4">
                <CodeBlock
                  testId="apidocs-auth-code"
                  code={`Authorization: Bearer lm_sk_live_xxxxxxxxxxxx`}
                  lang="http"
                />
              </div>
              <p className="mt-3 text-sm text-lm-text3">
                Keys are workspace-scoped and inherit that workspace's plan quota. Revoke a leaked key from the dashboard — new requests fail instantly.
              </p>
            </section>

            {/* Endpoints */}
            <section id="endpoints" className="scroll-mt-24">
              <h2 className="font-display text-2xl font-semibold text-lm-text mb-4">Endpoints reference</h2>
              <div className="space-y-3">
                <EndpointCard method="POST" path="/v1/scans" desc="Kick off a new scan across the selected platforms. Returns a scan_id and the ranked results synchronously (blocking; typical latency 8–20s)." testId="ep-post-scans" />
                <EndpointCard method="GET" path="/v1/scans/:scan_id" desc="Retrieve a completed scan with full result payload — intent classification, opportunity scores, sources." testId="ep-get-scan" />
                <EndpointCard method="GET" path="/v1/scans" desc="List recent scans for the authenticated workspace. Paginated with ?limit=&cursor=." testId="ep-list-scans" />
                <EndpointCard method="GET" path="/v1/leads" desc="List saved leads. Filter by ?brand_id=, ?platform=, ?min_score=." testId="ep-list-leads" />
                <EndpointCard method="POST" path="/v1/leads" desc="Save a lead from a scan result. Body: {result_id, notes?}." testId="ep-post-leads" />
                <EndpointCard method="GET" path="/v1/workspace" desc="Return your workspace metadata: name, plan, quota, remaining scans." testId="ep-get-ws" />
              </div>
            </section>

            {/* Rate limits */}
            <section id="rate-limits" className="scroll-mt-24">
              <h2 className="font-display text-2xl font-semibold text-lm-text mb-3">Rate limits</h2>
              <div className="rounded-lg border border-lm-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-lm-tint/60 text-lm-text3">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-medium">Plan</th>
                      <th className="text-right px-4 py-2.5 font-medium">Scans / day</th>
                      <th className="text-right px-4 py-2.5 font-medium">Requests / min</th>
                    </tr>
                  </thead>
                  <tbody className="bg-lm-surface">
                    <tr className="border-t border-lm-border/60"><td className="px-4 py-2.5">Free</td><td className="px-4 py-2.5 text-right font-mono">3</td><td className="px-4 py-2.5 text-right font-mono">10</td></tr>
                    <tr className="border-t border-lm-border/60"><td className="px-4 py-2.5">Starter</td><td className="px-4 py-2.5 text-right font-mono">30</td><td className="px-4 py-2.5 text-right font-mono">30</td></tr>
                    <tr className="border-t border-lm-border/60"><td className="px-4 py-2.5">Growth</td><td className="px-4 py-2.5 text-right font-mono">120</td><td className="px-4 py-2.5 text-right font-mono">60</td></tr>
                    <tr className="border-t border-lm-border/60"><td className="px-4 py-2.5">Agency</td><td className="px-4 py-2.5 text-right font-mono">400</td><td className="px-4 py-2.5 text-right font-mono">120</td></tr>
                    <tr className="border-t border-lm-border/60"><td className="px-4 py-2.5">Lifetime / LTD</td><td className="px-4 py-2.5 text-right font-mono">as-published</td><td className="px-4 py-2.5 text-right font-mono">60</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-sm text-lm-text3">
                Exceeded limits return <code className="font-mono text-xs bg-lm-tint px-1 py-0.5 rounded">429 Too Many Requests</code> with a <code className="font-mono text-xs bg-lm-tint px-1 py-0.5 rounded">Retry-After</code> header.
              </p>
            </section>

            {/* Errors */}
            <section id="errors" className="scroll-mt-24">
              <h2 className="font-display text-2xl font-semibold text-lm-text mb-3">Error codes</h2>
              <div className="rounded-lg border border-lm-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-lm-tint/60 text-lm-text3">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-medium">Status</th>
                      <th className="text-left px-4 py-2.5 font-medium">Meaning</th>
                      <th className="text-left px-4 py-2.5 font-medium">Common cause</th>
                    </tr>
                  </thead>
                  <tbody className="bg-lm-surface">
                    {[
                      ["400", "Bad Request", "Missing/invalid field in JSON body."],
                      ["401", "Unauthorized", "Missing, malformed or revoked API key."],
                      ["402", "Payment Required", "Scan quota exhausted on the current plan."],
                      ["403", "Forbidden", "Key valid, but action not permitted for this workspace."],
                      ["404", "Not Found", "scan_id / lead_id doesn't exist for this workspace."],
                      ["422", "Unprocessable", "Payload validated but semantically invalid (e.g. unknown platform)."],
                      ["429", "Too Many Requests", "Rate limit hit — retry after the header value."],
                      ["500", "Server Error", "Something went wrong on our side. Rare, always logged."],
                    ].map((r) => (
                      <tr key={r[0]} className="border-t border-lm-border/60">
                        <td className="px-4 py-2.5 font-mono">{r[0]}</td>
                        <td className="px-4 py-2.5">{r[1]}</td>
                        <td className="px-4 py-2.5 text-lm-text2">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-sm text-lm-text3">
                Every error response has a JSON body: <code className="font-mono text-xs bg-lm-tint px-1 py-0.5 rounded">{`{"detail": "human-readable message"}`}</code>.
              </p>
            </section>

            {/* Examples */}
            <section id="examples" className="scroll-mt-24">
              <h2 className="font-display text-2xl font-semibold text-lm-text mb-3">Examples</h2>
              <div className="space-y-6">
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3 mb-2">curl</div>
                  <CodeBlock code={curlExample} lang="bash" testId="apidocs-curl-copy" />
                </div>
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3 mb-2">Python</div>
                  <CodeBlock code={pyExample} lang="python" testId="apidocs-py-copy" />
                </div>
              </div>
              <div className="mt-8 rounded-2xl border border-lm-border bg-lm-surface p-6 text-center">
                <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3">Ready to build?</div>
                <h3 className="mt-1 font-display text-xl font-semibold text-lm-text">Grab a key and hit /v1/scans</h3>
                <Link
                  to="/signup"
                  data-testid="apidocs-cta-signup"
                  className="mt-4 inline-flex items-center gap-2 bg-lm-primary hover:bg-lm-primaryHover text-white text-sm font-semibold px-5 py-2.5 rounded-lg"
                >
                  Get started <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </section>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
